import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isLtrOnlyPath } from "../utils/ltrOnlyRoutes";
import { switchLanguage, Locale } from "../redux/languageSlice";
import type { RootState, AppDispatch } from "../redux/store";

// ─── Arabic plural rules (CLDR: zero / one / two / few / many) ──────
// Arabic nouns inflect by count, so a naive `${n} درس` reads wrong for most
// numbers. `arCount` picks the right form and places the numeral only where
// Arabic actually uses one — "درسان" (2), "3 دروس" (3–10), "15 درساً" (11+).
interface ArForms {
  zero?: string;   // rendered as-is, e.g. "لا دروس"
  one: string;     // rendered as-is, e.g. "درس واحد"
  two: string;     // rendered as-is, e.g. "درسان"
  few: string;     // prefixed with the numeral, e.g. "دروس"  -> "3 دروس"
  many: string;    // prefixed with the numeral, e.g. "درساً" -> "15 درساً"
}

const arCount = (n: number, f: ArForms): string => {
  const rest = n % 100;
  if (n === 0) return f.zero ?? `${n} ${f.many}`;
  if (n === 1) return f.one;
  if (n === 2) return f.two;
  if (rest >= 3 && rest <= 10) return `${n} ${f.few}`;
  return `${n} ${f.many}`;
};

// Counted nouns reused across the course screens.
const AR_COURSE  : ArForms = { zero: "لا توجد دورات", one: "دورة واحدة",  two: "دورتان",    few: "دورات",    many: "دورة" };
const AR_LESSON  : ArForms = { zero: "لا توجد دروس",  one: "درس واحد",   two: "درسان",     few: "دروس",     many: "درساً" };
const AR_PHASE   : ArForms = { zero: "لا توجد مراحل", one: "مرحلة واحدة", two: "مرحلتان",   few: "مراحل",    many: "مرحلة" };
const AR_VIDEO   : ArForms = { zero: "لا توجد فيديوهات", one: "فيديو واحد", two: "فيديوهان", few: "فيديوهات", many: "فيديو" };
const AR_QUESTION: ArForms = { zero: "لا توجد أسئلة",  one: "سؤال واحد",  two: "سؤالان",    few: "أسئلة",    many: "سؤالاً" };
const AR_LEAFLET : ArForms = { zero: "لا توجد منشورات", one: "منشور واحد", two: "منشوران",   few: "منشورات",  many: "منشوراً" };
const AR_PAGE    : ArForms = { zero: "لا توجد صفحات",  one: "صفحة واحدة", two: "صفحتان",    few: "صفحات",    many: "صفحة" };
const AR_MINUTE  : ArForms = { one: "دقيقة واحدة",     two: "دقيقتان",   few: "دقائق",     many: "دقيقة" };

// ─── Translation shape ──────────────────────────────────────────
export interface DrPawT {
  nav: {
    // membership nav
    dashboard: string;
    classes: string;
    coaching: string;
    foundation: string;
    favorites: string;
    members: string;
    login: string;
    signup: string;
    logout: string;
    profile: string;
    myAccount: string;
    // public nav
    home: string;
    pricing: string;
    about: string;
    contact: string;
    startLearning: string;
    emergencyLine: string;
    emergencyLine24: string;
  };
  home: {
    hero: {
      badge: string;
      words: string[];
      highlight: string;
      ctaPrimary: string;
      ctaSecondary: string;
      stats: { label: string }[];
      highlights: string[];
      // legacy kept for any other usage
      title: string;
      titleHighlight: string;
      subtitle: string;
      cta: string;
      watchDemo: string;
    };
    ticker: string[];
    about: {
      sectionLabel: string;
      heading: string;
      headingHighlight: string;
      headingEnd: string;
      body: string;
      badgeLabel: string;
      highlights: string[];
      ctaExplore: string;
      ctaServices: string;
    };
    services: {
      sectionLabel: string;
      heading: string;
      headingHighlight: string;
      subtitle: string;
      learnMore: string;
      items: { title: string; desc: string }[];
    };
    stats: {
      items: { label: string; desc: string }[];
    };
    emergency: {
      badge: string;
      heading: string;
      headingHighlight: string;
      body: string;
      callNow: string;
      contactUs: string;
      locations: { name: string; hours: string; tag: string; mapUrl?: string }[];
    };
    pricing: {
      label: string;
      heading: string;
      headingHighlight: string;
      subtitle: string;
      monthly: string;
      yearly: string;
      saveBadge: string;
      billedAnnually: string;
      cancelAnytime: string;
      savePercent: (n: number) => string;
      mostPopular: string;
      includeVideos: string;
      includeVideosHint: (amount: number) => string;
      plans: {
        starter: { name: string; desc: string; cta: string; features: string[] };
        pro:     { name: string; desc: string; cta: string; features: string[] };
        elite:   { name: string; desc: string; cta: string; features: string[] };
      };
    };
    testimonials: {
      sectionLabel: string;
      heading: string;
      headingHighlight: string;
      subtitle: string;
      avgRating: string;
      reviews: string;
      recommend: string;
      roles: { owner: string; regular: string; longTime: string };
    };
    contact: {
      sectionLabel: string;
      heading: string;
      headingHighlight: string;
      subtitle: string;
      info: { title: string; body: string }[];
      quote: string;
      quoteAuthor: string;
      fields: { name: string; email: string; phone: string; subject: string; message: string };
      submit: string;
      sendAnother: string;
      successTitle: string;
      successBody: string;
      errorFill: string;
      errorEmail: string;
      errorMsg: string;
    };
    finalCta: {
      heading: string;
      headingHighlight: string;
      subtitle: string;
      subtitleHighlight: string;
      startLearning: string;
    };
  };
  classes: {
    chip: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    totalCourses: (n: number) => string;
    totalVideos:  (n: number) => string;
    totalLessons: (n: number) => string;
    continuelearning: string;
    myCourses: string;
    myCoursesSubtitle: (n: number) => string;
    discoverMore: string;
    browseCourses: string;
    discoverSubtitle: (n: number) => string;
    browseSubtitle: (n: number) => string;
    moreAvailable: string;
    exploreButton: string;
    unlockButton: string;
    subscribeButton: string;
    search: string;
    sortMostPopular: string;
    sortNewest: string;
    sortMostLessons: string;
    sortMostVideos: string;
    locked: string;
    unlocked: string;
    noResults: string;
    noResultsSubtitle: string;
    resetSearch: string;
    subscribeTitle: string;
    subscribeSubtitle: string;
    viewPlans: string;
    active: string;
    planBanner: (plan: string, count: number) => string;
    unlockHint: string;
    phases: string;
    lessons: string;
    videos: string;
    quickStart: string;
    // counted variants — Arabic inflects the noun by number
    phasesCount: (n: number) => string;
    lessonsCount: (n: number) => string;
    videosCount: (n: number) => string;
    minutes: (n: number) => string;
    hoursMinutes: (h: number, m: number) => string;
    fallbackDescription: string;
    unlockShort: string;
    subscribeShort: string;
    openDetails: string;
    loadFailed: string;
    percentComplete: (pct: number) => string;
    // Unlock dialog — pick whether the companion video comes with the leaflet
    unlockDialog: {
      title: string;
      subtitle: string;
      leaflet: string;
      withVideo: string;
      withVideoHint: (amount: number) => string;
      total: string;
      continueToPayment: string;
    };
    // Companion video add-on on a course already owned
    videoIncluded: string;
    addVideo: (amount: number) => string;
    addVideoShort: (amount: number) => string;
    addVideoHint: string;
  };
  courseDetail: {
    backToCourses: string;
    premiumCourse: string;
    fallbackDescription: string;
    seriesNote: string;
    notFound: string;
    loadFailed: string;
    lockedBanner: string;
    lockedReason: (reason: string) => string;
    lockedError: string;
    phasesChip: (n: number) => string;
    videosChip: (n: number) => string;
    completedChip: (done: number, total: number) => string;
    courseProgress: string;
    curriculum: string;
    curriculumSubtitle: string;
    phasesSummary: (n: number) => string;
    lessonsSummary: (n: number) => string;
    liveSession: string;
    liveNow: string;
    scheduled: string;
    joinMeeting: string;
    nowPlaying: string;
    itemTypes: { video: string; pdf: string; letter: string; quiz: string; survey: string };
    pagesMeta: (n: number) => string;
    questionsMeta: (n: number) => string;
    actions: {
      resume: string; watch: string; open: string; read: string;
      startQuiz: string; takeSurvey: string;
    };
    // Companion video — the paid add-on that ships with a PDF leaflet
    companion: {
      label: string;
      addOn: string;
      watch: string;
      locked: string;
      lockedHint: string;
      processing: string;
      buy: (amount: number) => string;
      buyHint: (amount: number) => string;
    };
    markComplete: string;
    markIncomplete: string;
    continueLearning: string;
    continueSubtitle: string;
    totalLessons: string;
    completedLabel: string;
    estimatedTime: string;
    continueCta: {
      allDone: string; video: string; pdf: string; letter: string;
      quiz: string; survey: string;
    };
    comments: {
      heading: string;
      subtitle: string;
      placeholder: string;
      post: string;
      empty: string;
      reply: string;
      replyTo: (name: string) => string;
      deleteConfirm: string;
    };
    duration: {
      quickStart: string;
      minutes: (n: number) => string;
      hoursMinutes: (h: number, m: number) => string;
    };
    viewer: {
      lessonFallbackTitle: string;
      contentFallbackTitle: string;
      pdfFallbackTitle: string;
      letterFallbackTitle: string;
      fullscreen: string;
      close: string;
      open: string;
      rotateHint: string;
      videoUnavailable: string;
      pdfLoadFailed: string;
      pdfLabel: string;
      englishVersion: string;
      arabicVersion: string;
      // Shared PDF viewer chrome (also used by the admin preview)
      pdfLoading: string;
      pdfInlineUnavailable: string;
      pdfOpenNewTab: string;
      pdfFullscreen: string;
      pdfExitFullscreen: string;
      pdfDocument: string;
      pdfMobileHint: string;
      pdfOpenButton: string;
      pdfMobileFooter: string;
    };
    survey: {
      fallbackTitle: string;
      gradedQuiz: string;
      survey: string;
      questionCount: (n: number) => string;
      passingScore: (n: number) => string;
      passed: string;
      notPassed: string;
      passedBody: string;
      failedBody: (n: number) => string;
      correctOf: (correct: number, total: number) => string;
      reviewAnswers: string;
      yourAnswer: string;
      correctAnswer: string;
      noAnswer: string;
      notAutoGraded: string;
      retakeQuiz: string;
      close: string;
      thankYou: string;
      thankYouBody: string;
      submitAgain: string;
      questionFallback: (n: number) => string;
      typeAnswer: string;
      chooseOption: string;
      cancel: string;
      submit: string;
      submitting: string;
      submitFailed: string;
    };
  };
  dashboard: {
    welcomeBack: string;
    goodMorning: string;
    goodAfternoon: string;
    goodEvening: string;
    subtitle: string;
    totalVideos: string;
    unlocked: string;
    favorites: string;
    categories: string;
    advancedLibrary: string;
    myFavorites: string;
    premiumContent: string;
    savedForAccess: string;
    freePlan: string;
    freePlanSubtitle: string;
    activePlan: string;
    fullAccess: string;
    upgradePlan: string;
    browseClasses: string;
    noVideos: string;
    noVideosSubtitle: string;
    noFavorites: string;
    noFavoritesHint: string;
    foundation: string;
    foundationDesc: string;
    coaching: string;
    coachingDesc: string;
    classes: string;
    classesDesc: string;
    favoritesDesc: string;
    videosChip: (n: number) => string;
  };
  auth: {
    backHome: string;
    login: {
      metaTitle: string;
      metaDescription: string;
      title: string;
      subtitle: string;
      email: string;
      password: string;
      rememberMe: string;
      forgotPassword: string;
      loginButton: string;
      sessionExpired: string;
      failed: string;
      noAccount: string;
      signupLink: string;
    };
    signup: {
      metaTitle: string;
      metaDescription: string;
      title: string;
      subtitle: string;
      fullName: string;
      email: string;
      password: string;
      confirmPassword: string;
      agreePrefix: string;
      terms: string;
      and: string;
      privacy: string;
      createAccount: string;
      haveAccount: string;
      signIn: string;
      successTitle: string;
      successSubtitle: string;
      passwordMismatch: string;
      mustAgree: string;
      failed: string;
      // Password-strength meter labels
      strength: { weak: string; fair: string; good: string; strong: string };
      // Trust badges under the submit button
      badges: { secure: string; free: string; noCard: string };
    };
  };
  courseSelection: {
    back: string;
    titleOne: string;
    titleMany: string;
    subtitleOne: string;
    subtitleMany: (max: number) => string;
    planLeaflets: (n: number) => string;
    counter: (selected: number, max: number) => string;
    owned: string;
    openOwned: string;
    select: string;
    selected: string;
    limitReached: (max: number) => string;
    videoAvailable: string;
    fallbackDescription: string;
    empty: string;
    loadFailed: string;
    retry: string;
    pickPrompt: string;
    confirm: string;
    // Companion-video add-on
    withVideo: string;
    withVideoHint: (amount: number) => string;
    videoLocked: string;
    total: string;
    totalHint: (base: number, videos: number, addon: number) => string;
  };
  common: {
    loading: string;
    error: string;
    cancel: string;
    save: string;
    close: string;
    back: string;
    next: string;
    view: string;
    explore: string;
    perMonth: string;
  };
}

// ─── English translations ───────────────────────────────────────
const en: DrPawT = {
  nav: {
    dashboard:       "Dashboard",
    classes:         "Classes",
    coaching:        "Coaching",
    foundation:      "Foundation",
    favorites:       "Favorites",
    members:         "Members",
    login:           "Log In",
    signup:          "Sign Up",
    logout:          "Logout",
    profile:         "Profile",
    myAccount:       "My Account",
    home:            "Home",
    pricing:         "Pricing",
    about:           "About Us",
    contact:         "Contact",
    startLearning:   "Start Learning",
    emergencyLine:   "",
    emergencyLine24: "",
  },
  home: {
    hero: {
      badge:          "🐾 Vet Learning for Every Animal Lover",
      words:          ["Learn", "Vet", "Care"],
      highlight:      "from Experts",
      ctaPrimary:     "Start Learning Free",
      ctaSecondary:   "Explore Courses",
      stats:          [{ label: "Courses" }, { label: "Students" }, { label: "Video Lessons" }],
      highlights:     ["Get expert guidance by real, passionate vets", "⁠Go at whatever pace works for you", "⁠Finish the program and earn your certificate"],
      title:          "Your Pet Deserves",
      titleHighlight: "the Best Care",
      subtitle:       "Expert veterinary education for modern pet owners.",
      cta:            "Start Learning",
      watchDemo:      "Watch Demo",
    },
    ticker: ["", "General Veterinary Care", "", "Dental Care", "Diagnostic Services", "Pet Grooming", "Nutrition Center", "ICU & Hospitalization", "Preventive Medicine", "Vaccinations"],
    about: {
      sectionLabel:     "About Dr. Paw",
      heading:          "Over 10 Years of",
      headingHighlight: "Compassionate",
      headingEnd:       "Veterinary Care",
      body:             "At Dr Paw we believe that pets are our companions and part of our life. At all stages of your fur baby's life we will offer our medical and nutritional expertise to raise a happy and healthy pet. We are the specialists who know the history of your pet and we make use of our know-how to constantly improve his or her quality of life.",
      badgeLabel:       "Years of Care",
      highlights:       ["General Veterinary Care", "Diagnostic Services", "Advanced Surgery & Anesthesia", "Dental Care", "Internal Medicine & Specialized Care", "Hospitalization & Intensive Care"],
      ctaExplore:       "Explore Courses",
      ctaServices:      "Our Services",
    },
    services: {
      sectionLabel:     "What We Offer",
      heading:          "Comprehensive Veterinary",
      headingHighlight: "Services",
      subtitle:         "From preventive wellness to advanced medical care — our expert team is committed to your fur baby’s health and comfort through every stage of life.",
      learnMore:        "Learn more",
      items: [
        { title: "General Veterinary Care",                      desc: "Comprehensive everyday veterinary care focused on maintaining your pet's health, preventing disease, and supporting lifelong well-being." },
        { title: "Nutrition & Dietary Consultation",             desc: "Personalized nutritional guidance and in-depth dietary consultations tailored to your pet's age, lifestyle, medical conditions, and long-term health needs." },
        { title: "Dental Care & Surgery",                        desc: "Professional dental treatments and surgical procedures performed with precision, safety, and compassionate care to support your pet's overall health and comfort." },
        { title: "Diagnostic Imaging & Laboratory Services",     desc: "Advanced diagnostic tools including digital X-rays, ultrasound imaging, and in-house blood testing allow us to diagnose and monitor your pet's condition quickly and accurately." },
        { title: "Pet Shop & Essentials",                        desc: "A carefully selected range of premium pet products including high-quality nutrition, toys, accessories, travel carriers, grooming essentials, and everyday must-haves for your beloved fur baby." },
      ],
    },
    stats: {
      items: [
        { label: "Years of Excellence",    desc: "Serving Lebanon since 1996" },
        { label: "Professional Doctors",   desc: "Specialists across all fields" },
        { label: "Pets Treated",           desc: "And counting every year" },
        { label: "Clinic Location",         desc: "Dbayeh branch" },
      ],
    },
    emergency: {
      badge:           "",
      heading:         "Expert Veterinary Care for Every Stage of Life",
      headingHighlight:"",
      body:            "We combine advanced veterinary medicine with genuine compassion to provide personalized care for every pet. Whether it’s preventive healthcare, treatment, or ongoing support, we’re here to help your fur babies live their healthiest life.",
      callNow:         "Call Now",
      contactUs:       "Contact Us",
      locations: [
        { name: "Dbayeh", hours: "Dbayeh 670  |  04 541 965  |  70 886 078", tag: "Find Us Here", mapUrl: "https://maps.app.goo.gl/SAHkXkD7sNC5Bf2p6" },
      ],
    },
    pricing: {
      label:           "Pricing Plans",
      heading:         "Invest in Your Pet's",
      headingHighlight:"Future",
      subtitle:        "Choose the plan that fits your learning goals. Cancel anytime — no long-term commitment.",
      monthly:         "Monthly",
      yearly:          "Yearly",
      saveBadge:       "SAVE 35%",
      billedAnnually:  "Billed annually · Cancel anytime",
      cancelAnytime:   "Cancel anytime",
      savePercent:     (n) => `Save ${n}%`,
      mostPopular:     "Most Popular",
      includeVideos:     "Include all companion videos",
      includeVideosHint: (amount) => `+$${amount}/mo`,
      plans: {
        starter: { name: "Individual Leaflet", desc: "Access one professionally designed veterinary educational leaflet created to help pet parents better understand and care for their pets.", cta: "Get Started", features: ["1 downloadable educational leaflet", "Practical, evidence-based information", "Clear and easy-to-follow guidance"] },
        pro:     { name: "Leaflet Package",    desc: "Choose any three veterinary educational leaflets and enjoy expanded learning at a preferred rate.", cta: "Choose Leaflets", features: ["Access to any 3 leaflets", "Covers multiple pet health topics", "Greater value and broader education"] },
        elite:   { name: "Unlimited Monthly Access", desc: "Get unlimited access to the full educational library along with personalized veterinary guidance.", cta: "Go Unlimited", features: ["Unlimited access to all educational leaflets for 1 month", "Includes 1 private online consultation", "Access to newly released content during membership period", "Priority educational support"] },
      },

    },
    testimonials: {
      sectionLabel:     "Client Reviews",
      heading:          "What Our Clients Say",
      headingHighlight: "About Us",
      subtitle:         "Don't just take our word for it — hear from the pet parents who trust us with their beloved companions.",
      avgRating:        "Average Rating",
      reviews:          "Reviews",
      recommend:        "Recommend Us",
      roles:            { owner: "Pet Owner", regular: "Regular Client", longTime: "Long-time Client" },
    },
    contact: {
      sectionLabel:     "Get In Touch",
      heading:          "Let's Care for Your",
      headingHighlight: "Fur Baby Together",
      subtitle:         "Whether you need medical advice, nutritional guidance, or help booking an appointment, our team is here to support you and your fur baby every step of the way.",
      info: [
        { title: "Email Us",       body: "Our team reads every message personally and replies with care." },
        { title: "Response Time",  body: "We typically respond within 1–2 business days." },
        { title: "What to Ask",    body: "Questions about courses, plans, or anything learning-related — we're here for it." },
      ],
      quote:       "Every pet deserves exceptional care. We're here to help.",
      quoteAuthor: "— The Dr. Paw Team",
      fields:      { name: "Your Name", email: "Email Address", phone: "Phone (optional)", subject: "Subject", message: "Your Message" },
      submit:      "Send Message",
      sendAnother: "Send Another Message",
      successTitle:"Message Sent!",
      successBody: "Thank you for reaching out. We'll be in touch soon.",
      errorFill:   "Please fill in all fields.",
      errorEmail:  "Please enter a valid email address.",
      errorMsg:    "Message must be at least 10 characters.",
    },
    finalCta: {
      heading:          "Ready to Book Your",
      headingHighlight: "Learning Journey?",
      subtitle:         "Schedule a visit today and give your pet the compassionate, expert care they deserve. We're here for you —",
      subtitleHighlight:"every day, all day.",
      startLearning:    "Start Learning",
    },
  },
  classes: {
    chip:           "Learn with Dr. Paw",
    title:          "Learn with",
    titleHighlight: "Dr. Paw",
    subtitle:       "Practical, science-based lessons designed to help pet parents provide healthier, happier lives for their fur babies.",
    totalCourses:   (n: number) => `${n} Courses`,
    totalVideos:    (n: number) => `${n} Videos`,
    totalLessons:   (n: number) => `${n} Lessons`,
    continuelearning:"Continue Learning",
    myCourses:      "My Courses",
    myCoursesSubtitle: (n) => `${n} course${n !== 1 ? "s" : ""} in your library`,
    discoverMore:   "Discover More",
    browseCourses:  "Browse Courses",
    discoverSubtitle: (n) => `${n} course${n !== 1 ? "s" : ""} available to add to your collection`,
    browseSubtitle:   (n) => `${n} course${n !== 1 ? "s" : ""} — subscribe to unlock access`,
    moreAvailable:  "More Available",
    exploreButton:  "Explore Course",
    unlockButton:   "Unlock Course",
    subscribeButton:"Subscribe to Unlock",
    search:         "Search courses by title or description",
    sortMostPopular:"Most Popular",
    sortNewest:     "Newest",
    sortMostLessons:"Most Lessons",
    sortMostVideos: "Most Videos",
    locked:         "Locked",
    unlocked:       "Unlocked",
    noResults:      "No courses found",
    noResultsSubtitle: "Try a different keyword or clear the current search.",
    resetSearch:    "Reset Search",
    subscribeTitle: "Subscribe to unlock courses",
    subscribeSubtitle: "Choose a plan to access our veterinary course library",
    viewPlans:      "View Plans",
    active:         "Active",
    planBanner:     (plan, count) => `${plan} Plan — ${count} course${count !== 1 ? "s" : ""} unlocked`,
    unlockHint:     `Click "Unlock Course" on any locked course to add it to your collection`,
    phases:         "Phases",
    lessons:        "Lessons",
    videos:         "Videos",
    quickStart:     "Quick Start",
    phasesCount:    (n) => `${n} ${n === 1 ? "Phase" : "Phases"}`,
    lessonsCount:   (n) => `${n} ${n === 1 ? "Lesson" : "Lessons"}`,
    videosCount:    (n) => `${n} ${n === 1 ? "Video" : "Videos"}`,
    minutes:        (n) => `${n} min`,
    hoursMinutes:   (h, m) => `${h}h ${m}m`,
    fallbackDescription: "A complete, step-by-step curriculum focused on consistent visible progress.",
    unlockShort:    "Unlock",
    subscribeShort: "Subscribe",
    openDetails:    "Open course details",
    loadFailed:     "Failed to load courses",
    percentComplete: (pct) => `${pct}% complete`,
    unlockDialog: {
      title:             "Unlock this course",
      subtitle:          "The leaflet is yours to keep. Add its companion video now, or come back for it any time.",
      leaflet:           "Leaflet",
      withVideo:         "Add the companion video",
      withVideoHint:     (amount) => `+$${amount}`,
      total:             "Total",
      continueToPayment: "Continue to payment",
    },
    videoIncluded:  "Video included",
    addVideo:       (amount) => `Add companion video · $${amount}`,
    addVideoShort:  (amount) => `Add video · $${amount}`,
    addVideoHint:   "Watch the video that goes with this leaflet",
  },
  courseDetail: {
    backToCourses:     "Back To Courses",
    premiumCourse:     "Premium Course",
    fallbackDescription:
      "A complete, premium learning path designed for measurable results and consistent progress.",
    seriesNote:
      "Every guide is written by Dr. Mira Tabet, veterinarian and animal nutritionist, so collect the series and have trusted answers ready for every stage of your pet's life.",
    notFound:          "Course not found",
    loadFailed:        "Failed to load course",
    lockedBanner:      "This course is locked by admin.",
    lockedReason:      (reason) => `Reason: ${reason}`,
    lockedError:       "This course is currently locked by admin.",
    phasesChip:        (n) => `${n} ${n === 1 ? "Phase" : "Phases"}`,
    videosChip:        (n) => `${n} ${n === 1 ? "Video" : "Videos"}`,
    completedChip:     (done, total) => `${done}/${total} Completed`,
    courseProgress:    "Course Progress",
    curriculum:        "Course Curriculum",
    curriculumSubtitle:"Follow each phase in order. Mark each lesson complete to track your progress.",
    phasesSummary:     (n) => `${n} ${n === 1 ? "phase" : "phases"}`,
    lessonsSummary:    (n) => `${n} ${n === 1 ? "lesson" : "lessons"}`,
    liveSession:       "Live Session",
    liveNow:           "Live Now",
    scheduled:         "Scheduled",
    joinMeeting:       "Join Meeting",
    nowPlaying:        "NOW PLAYING",
    itemTypes: { video: "Video", pdf: "Pdf", letter: "Text", quiz: "Quiz", survey: "Survey" },
    pagesMeta:         (n) => `${n} ${n === 1 ? "page" : "pages"}`,
    questionsMeta:     (n) => `${n} ${n === 1 ? "question" : "questions"}`,
    actions: {
      resume: "Resume", watch: "Watch", open: "Open", read: "Read",
      startQuiz: "Start Quiz", takeSurvey: "Take Survey",
    },
    companion: {
      label:      "Companion video",
      addOn:      "Add-on",
      watch:      "Watch video",
      locked:     "Video locked",
      lockedHint: "The companion video is a paid add-on for this leaflet. Add it to your plan to watch it.",
      processing: "The companion video is still processing. Check back shortly.",
      buy:        (amount) => `Unlock video · $${amount}`,
      buyHint:    (amount) => `Add the companion video to this leaflet for $${amount}.`,
    },
    markComplete:      "Mark as done",
    markIncomplete:    "Mark incomplete",
    continueLearning:  "Continue Learning",
    continueSubtitle:  "Continue from your next incomplete lesson and keep your momentum.",
    totalLessons:      "Total Lessons",
    completedLabel:    "Completed",
    estimatedTime:     "Estimated Time",
    continueCta: {
      allDone: "All Lessons Completed",
      video:   "Continue Video Lesson",
      pdf:     "Continue PDF Lesson",
      letter:  "Continue Text Lesson",
      quiz:    "Take Quiz",
      survey:  "Take Survey",
    },
    comments: {
      heading:      "Community Comments",
      subtitle:     "Share your thoughts, ask questions, and like helpful comments from other learners.",
      placeholder:  "Write a comment...",
      post:         "Post",
      empty:        "No comments yet. Be the first to start the discussion.",
      reply:        "Reply",
      replyTo:      (name) => `Reply to ${name}...`,
      deleteConfirm:"Delete this comment?",
    },
    duration: {
      quickStart:   "Quick Start",
      minutes:      (n) => `${n} min`,
      hoursMinutes: (h, m) => `${h}h ${m}m`,
    },
    viewer: {
      lessonFallbackTitle:  "Course Lesson",
      contentFallbackTitle: "Lesson Content",
      pdfFallbackTitle:     "PDF Lesson",
      letterFallbackTitle:  "Text Lesson",
      fullscreen:      "Fullscreen",
      close:           "Close",
      open:            "Open",
      rotateHint:      "Rotate your phone to landscape for the best viewing experience.",
      videoUnavailable:"This lesson video is not available yet.",
      pdfLoadFailed:   "Failed to load PDF.",
      pdfLabel:        "PDF",
      englishVersion:  "English version",
      arabicVersion:   "Arabic version",
      pdfLoading:            "Loading PDF…",
      pdfInlineUnavailable:  "Unable to display PDF inline",
      pdfOpenNewTab:         "Open in new tab",
      pdfFullscreen:         "Fullscreen",
      pdfExitFullscreen:     "Exit fullscreen",
      pdfDocument:           "PDF Document",
      pdfMobileHint:         "Tap the button below to open this PDF in your device's viewer",
      pdfOpenButton:         "Open PDF",
      pdfMobileFooter:       "Opens in your browser's built-in PDF viewer",
    },
    survey: {
      fallbackTitle:  "Survey",
      gradedQuiz:     "Graded Quiz",
      survey:         "Survey",
      questionCount:  (n) => `${n} ${n === 1 ? "question" : "questions"}`,
      passingScore:   (n) => `Passing score: ${n}%`,
      passed:         "Passed!",
      notPassed:      "Not passed",
      passedBody:     "Great job! You've successfully passed this quiz.",
      failedBody:     (n) => `You need ${n}% to pass. Review the material and try again.`,
      correctOf:      (correct, total) => `${correct} / ${total} correct`,
      reviewAnswers:  "Review Your Answers",
      yourAnswer:     "Your answer:",
      correctAnswer:  "Correct answer:",
      noAnswer:       "No answer",
      notAutoGraded:  "Text answer — not auto-graded",
      retakeQuiz:     "Retake Quiz",
      close:          "Close",
      thankYou:       "Thank you!",
      thankYouBody:   "Your response has been submitted successfully.",
      submitAgain:    "Submit Again",
      questionFallback: (n) => `Question ${n}`,
      typeAnswer:     "Type your answer here...",
      chooseOption:   "Choose an option",
      cancel:         "Cancel",
      submit:         "Submit",
      submitting:     "Submitting...",
      submitFailed:   "Failed to submit. Please try again.",
    },
  },
  dashboard: {
    welcomeBack:     "Welcome back",
    goodMorning:     "Good morning",
    goodAfternoon:   "Good afternoon",
    goodEvening:     "Good evening",
    subtitle:        "Continue your pet care learning journey. Your courses are waiting.",
    totalVideos:     "Total Videos",
    unlocked:        "Unlocked",
    favorites:       "Favorites",
    categories:      "Categories",
    advancedLibrary: "Advanced Library",
    myFavorites:     "My Favorites",
    premiumContent:  "Premium content organized by category",
    savedForAccess:  "Videos you've saved for quick access",
    freePlan:        "You're on the Free plan",
    freePlanSubtitle:"Upgrade to unlock all premium videos and courses.",
    activePlan:      "Plan — Active",
    fullAccess:      "Enjoy your full access to all content.",
    upgradePlan:     "Upgrade Plan",
    browseClasses:   "Browse All Classes",
    noVideos:        "No videos found",
    noVideosSubtitle:"Check back soon — new content is added regularly.",
    noFavorites:     "No favorites yet",
    noFavoritesHint: "Press the ❤️ on any video to save it here.",
    foundation:      "Foundation",
    foundationDesc:  "Build the base",
    coaching:        "Coaching",
    coachingDesc:    "Live sessions",
    classes:         "Classes",
    classesDesc:     "Full library",
    favoritesDesc:   "Saved videos",
    videosChip:      (n) => `${n} videos`,
  },
  auth: {
    backHome: "Home",
    login: {
      metaTitle:       "Sign In — Dr.Paw Veterinary Education",
      metaDescription: "Sign in to your Dr.Paw account to access your veterinary courses, CE modules, and masterclasses.",
      title:          "Welcome Back",
      subtitle:       "Sign in to access your courses and continue learning.",
      email:          "Email Address",
      password:       "Password",
      rememberMe:     "Remember me",
      forgotPassword: "Forgot password?",
      loginButton:    "Sign In",
      sessionExpired: "Your session expired. Please sign in again.",
      failed:         "Login failed. Please check your credentials.",
      noAccount:      "Don't have an account?",
      signupLink:     "Create one free →",
    },
    signup: {
      metaTitle:       "Create Account — Dr.Paw Pet Care Courses",
      metaDescription: "Join Dr.Paw today — free to sign up. Get vet-written pet-care courses and join a community of pet parents.",
      title:           "Create Account",
      subtitle:        "Join free and start learning from certified veterinarians.",
      fullName:        "Full Name",
      email:           "Email Address",
      password:        "Password",
      confirmPassword: "Confirm Password",
      agreePrefix:     "I agree to the",
      terms:           "Terms",
      and:             "and",
      privacy:         "Privacy Policy",
      createAccount:   "Create My Account",
      haveAccount:     "Already have an account?",
      signIn:          "Sign in →",
      successTitle:    "You're in! 🎉",
      successSubtitle: "Redirecting to your courses…",
      passwordMismatch: "Passwords do not match.",
      mustAgree:        "Please agree to the Terms & Privacy Policy.",
      failed:           "Registration failed. Please try again.",
      strength: { weak: "Weak", fair: "Fair", good: "Good", strong: "Strong" },
      badges:   { secure: "Secure", free: "Free Account", noCard: "No Card" },
    },
  },
  courseSelection: {
    back:                "Back to plans",
    titleOne:            "Choose Your Leaflet",
    titleMany:           "Choose Your Leaflets",
    subtitleOne:         "Read what's inside each guide, then pick the one you want to unlock.",
    subtitleMany:        (max) => `Read what's inside each guide, then pick up to ${max} to unlock.`,
    planLeaflets:        (n) => `${n} ${n === 1 ? "leaflet" : "leaflets"}`,
    counter:             (selected, max) => `${selected}/${max} in cart`,
    owned:               "Already yours",
    openOwned:           "Open leaflet",
    select:              "Add to cart",
    selected:            "In your cart",
    limitReached:        (max) => `You've picked ${max}. Remove one to choose another.`,
    videoAvailable:      "Companion video available",
    fallbackDescription: "No description available",
    empty:               "No leaflets are available right now. Please check back soon.",
    loadFailed:          "We couldn't load the leaflets.",
    retry:               "Try again",
    pickPrompt:          "Add a leaflet to your cart to continue",
    confirm:             "Go to checkout",
    withVideo:           "Add the companion video",
    withVideoHint:       (amount) => `+$${amount}`,
    videoLocked:         "Select this leaflet first",
    total:               "Total",
    totalHint:           (base, videos, addon) =>
      videos > 0
        ? `$${base} + ${videos} × $${addon} video`
        : `$${base}, no video add-ons`,
  },
  common: {
    loading:  "Loading...",
    error:    "An error occurred",
    cancel:   "Cancel",
    save:     "Save",
    close:    "Close",
    back:     "Back",
    next:     "Next",
    view:     "View",
    explore:  "Explore",
    perMonth: "",
  },
};

// ─── Arabic translations (Modern Standard Arabic — correct & formal) ──
const ar: DrPawT = {
  nav: {
    dashboard:       "لوحة التحكم",
    classes:         "الدورات",
    coaching:        "التدريب",
    foundation:      "الأساسيات",
    favorites:       "المفضلة",
    members:         "الأعضاء",
    login:           "تسجيل الدخول",
    signup:          "إنشاء حساب",
    logout:          "تسجيل الخروج",
    profile:         "الملف الشخصي",
    myAccount:       "حسابي",
    home:            "الرئيسية",
    pricing:         "الأسعار",
    about:           "من نحن",
    contact:         "اتصل بنا",
    startLearning:   "ابدأ التعلم",
    emergencyLine:   "",
    emergencyLine24: "",
  },
  home: {
    hero: {
      badge:          "🐾 تعلم البيطرة لكل محب للحيوانات",
      words:          ["تعلّم", "الرعاية", "البيطرية"],
      highlight:      "من الخبراء",
      ctaPrimary:     "ابدأ التعلم مجاناً",
      ctaSecondary:   "استكشاف الدورات",
      stats:          [{ label: "دورة" }, { label: "طالب" }, { label: "درس فيديو" }],
      highlights:     ["احصل على إرشاد متخصص من أطباء بيطريين حقيقيين ومتحمسين", "تعلّم بالسرعة التي تناسبك", "أكمل البرنامج واحصل على شهادتك"],
      title:          "حيوانك الأليف يستحق",
      titleHighlight: "أفضل رعاية",
      subtitle:       "تعليم بيطري متخصص لأصحاب الحيوانات الأليفة.",
      cta:            "ابدأ التعلم",
      watchDemo:      "شاهد العرض",
    },
    ticker: ["", "رعاية بيطرية عامة", "", "رعاية الأسنان", "خدمات التشخيص", "تجميل الحيوانات", "مركز التغذية", "العناية المركزة", "الطب الوقائي", "التطعيمات"],
    about: {
      sectionLabel:     "عن دكتور باو",
      heading:          "أكثر من 10 سنوات من",
      headingHighlight: "الرعاية الرحيمة",
      headingEnd:       "البيطرية",
      body:             "في عيادة دكتور باو، نؤمن بأن الحيوانات الأليفة رفاقنا وجزء لا يتجزأ من حياتنا. في جميع مراحل حياة حيوانك الأليف، نقدم خبراتنا الطبية والتغذوية لضمان تربيته بصحة وسعادة. نحن متخصصون على دراية بتاريخ حيوانك الأليف، ونوظف خبراتنا لتحسين جودة حياته باستمرار.",
      badgeLabel:       "سنوات من الرعاية",
      highlights:       ["رعاية بيطرية عامة", "خدمات التشخيص", "جراحة متقدمة وتخدير", "رعاية الأسنان", "طب داخلي ورعاية متخصصة", "الإقامة والعناية المركزة"],
      ctaExplore:       "استكشاف الدورات",
      ctaServices:      "خدماتنا",
    },
    services: {
      sectionLabel:     "ما نقدمه",
      heading:          "خدمات بيطرية",
      headingHighlight: "شاملة",
      subtitle:         "من الرعاية الوقائية إلى الرعاية الطبية المتقدمة — فريقنا الخبير ملتزم بصحة وراحة حيوانك الأليف الصغير في كل مرحلة من مراحل حياته.",
      learnMore:        "اعرف المزيد",
      items: [
        { title: "الرعاية البيطرية العامة",                           desc: "رعاية بيطرية يومية شاملة تركز على الحفاظ على صحة حيوانك الأليف، والوقاية من الأمراض، ودعم رفاهيته طوال حياته." },
        { title: "استشارات التغذية والنظام الغذائي",                   desc: "إرشادات غذائية شخصية واستشارات غذائية متعمقة مصممة خصيصًا لعمر حيوانك الأليف ونمط حياته وحالته الصحية واحتياجاته الصحية على المدى الطويل." },
        { title: "العناية بالأسنان والجراحة",                          desc: "علاجات الأسنان والعمليات الجراحية الاحترافية التي تُجرى بدقة وأمان ورعاية حانية لدعم صحة حيوانك الأليف وراحته بشكل عام." },
        { title: "خدمات التصوير التشخيصي والمختبرات",                  desc: "تتيح لنا أدوات التشخيص المتقدمة، بما في ذلك الأشعة السينية الرقمية والتصوير بالموجات فوق الصوتية واختبارات الدم الداخلية، تشخيص حالة حيوانك الأليف ومراقبتها بسرعة ودقة." },
        { title: "متجر الحيوانات الأليفة والمستلزمات الأساسية",         desc: "مجموعة مختارة بعناية من منتجات الحيوانات الأليفة المميزة، بما في ذلك التغذية عالية الجودة، والألعاب، والإكسسوارات، وحقائب السفر، ومستلزمات العناية الشخصية، والضروريات اليومية لحيوانك الأليف المحبوب." },
      ],
    },
    stats: {
      items: [
        { label: "عام من التميز",            desc: "نخدم لبنان منذ عام 1996" },
        { label: "طبيب متخصص",               desc: "متخصصون في جميع المجالات" },
        { label: "حيوان أليف تمت معالجته",   desc: "والعدد يتزايد كل عام" },
        { label: "فروع",                     desc: "فرع صربا وزوق" },
      ],
    },
    emergency: {
      badge:           "",
      heading:         "رعاية بيطرية متخصصة في كل مرحلة من مراحل الحياة",
      headingHighlight:"",
      body:            "نجمع بين الطب البيطري المتقدم والرحمة الحقيقية لتقديم رعاية شخصية لكل حيوان أليف. سواء كان الأمر يتعلق بالرعاية الوقائية أو العلاج أو الدعم المستمر، نحن هنا لمساعدة حيواناتك الأليفة على عيش أصحى حياة.",
      callNow:         "اتصل الآن",
      contactUs:       "تواصل معنا",
      locations: [
        { name: "ضبية", hours: "ضبية 670  |  04 541 965  |  70 886 078", tag: "جدنا هنا", mapUrl: "https://maps.app.goo.gl/SAHkXkD7sNC5Bf2p6" },
      ],
    },
    pricing: {
      label:           "خطط الأسعار",
      heading:         "استثمر في مستقبل",
      headingHighlight:"حيوانك الأليف",
      subtitle:        "اختر الخطة التي تناسب أهدافك التعليمية. ألغِ اشتراكك في أي وقت — دون التزام طويل الأمد.",
      monthly:         "شهري",
      yearly:          "سنوي",
      saveBadge:       "وفّر 35%",
      billedAnnually:  "يُدفع سنوياً · إلغاء في أي وقت",
      cancelAnytime:   "إلغاء في أي وقت",
      savePercent:     (n) => `وفّر ${n}%`,
      mostPopular:     "الأكثر شعبية",
      includeVideos:     "أضف جميع الفيديوهات المرافقة",
      includeVideosHint: (amount) => `+$${amount}/شهر`,
      plans: {
        starter: { name: "منشور فردي",    desc: "احصل على منشور تعليمي بيطري واحد مصمم باحترافية لمساعدة أصحاب الحيوانات على فهم رعاية حيواناتهم بشكل أفضل.", cta: "ابدأ الآن",      features: ["منشور تعليمي واحد قابل للتنزيل", "معلومات عملية مبنية على الأدلة", "إرشادات واضحة وسهلة الاتباع"] },
        pro:     { name: "حزمة المنشورات", desc: "اختر أي ثلاثة منشورات تعليمية بيطرية واستمتع بتعلم موسّع بسعر مفضّل.", cta: "اختر المنشورات", features: ["الوصول إلى أي 3 منشورات", "يغطي مواضيع صحية متعددة", "قيمة أكبر وتعليم أشمل"] },
        elite:   { name: "وصول شهري غير محدود", desc: "احصل على وصول غير محدود إلى المكتبة التعليمية الكاملة مع إرشادات بيطرية شخصية.", cta: "اشترك الآن", features: ["وصول غير محدود لجميع المنشورات التعليمية لمدة شهر", "يشمل استشارة خاصة عبر الإنترنت", "الوصول إلى المحتوى الجديد خلال فترة العضوية", "دعم تعليمي ذو أولوية"] },
      },
    },
    testimonials: {
      sectionLabel:     "آراء العملاء",
      heading:          "ما يقوله عملاؤنا",
      headingHighlight: "عنّا",
      subtitle:         "لا تأخذ كلامنا فقط — اسمع من أصحاب الحيوانات الأليفة الذين يثقون بنا لرعاية رفاقهم الأعزاء.",
      avgRating:        "متوسط التقييم",
      reviews:          "تقييم",
      recommend:        "يوصون بنا",
      roles:            { owner: "صاحب حيوان أليف", regular: "عميل منتظم", longTime: "عميل دائم" },
    },
    contact: {
      sectionLabel:     "تواصل معنا",
      heading:          "لنعتني معاً",
      headingHighlight: "بحيوانك الأليف",
      subtitle:         "سواء كنت بحاجة إلى نصيحة طبية أو إرشادات غذائية أو مساعدة في حجز موعد، فإن فريقنا موجود هنا لدعمك أنت وحيوانك الأليف في كل خطوة على الطريق.",
      info: [
        { title: "راسلنا",         body: "فريقنا يقرأ كل رسالة بشكل شخصي ويرد باهتمام." },
        { title: "وقت الاستجابة",  body: "نرد عادةً خلال 1–2 أيام عمل." },
        { title: "ماذا تسأل",      body: "أسئلة حول الدورات أو الخطط أو أي شيء متعلق بالتعلم — نحن هنا من أجلك." },
      ],
      quote:       "كل حيوان أليف يستحق رعاية استثنائية. نحن هنا للمساعدة.",
      quoteAuthor: "— فريق Dr. Paw",
      fields:      { name: "اسمك", email: "البريد الإلكتروني", phone: "رقم الهاتف (اختياري)", subject: "الموضوع", message: "رسالتك" },
      submit:      "إرسال الرسالة",
      sendAnother: "إرسال رسالة أخرى",
      successTitle:"تم إرسال الرسالة!",
      successBody: "شكراً على تواصلك. سنتواصل معك قريباً.",
      errorFill:   "يرجى ملء جميع الحقول.",
      errorEmail:  "يرجى إدخال بريد إلكتروني صحيح.",
      errorMsg:    "يجب أن تتضمن الرسالة 10 أحرف على الأقل.",
    },
    finalCta: {
      heading:          "هل أنت مستعد لبدء",
      headingHighlight: "رحلة التعلم؟",
      subtitle:         "احجز زيارة اليوم وامنح حيوانك الأليف الرعاية المتخصصة التي يستحقها. نحن هنا لك —",
      subtitleHighlight:"كل يوم، طوال اليوم.",
      startLearning:    "ابدأ التعلم",
    },
  },
  classes: {
    chip:           "تعلّم مع دكتور باو",
    title:          "تعلّم مع",
    titleHighlight: "دكتور باو",
    subtitle:       "دروس عملية قائمة على العلم، مصممة لمساعدة أصحاب الحيوانات الأليفة على توفير حياة أكثر صحة وسعادة لحيواناتهم.",
    totalCourses:   (n) => arCount(n, AR_COURSE),
    totalVideos:    (n) => arCount(n, AR_VIDEO),
    totalLessons:   (n) => arCount(n, AR_LESSON),
    continuelearning:"تابع التعلم",
    myCourses:      "دوراتي",
    myCoursesSubtitle: (n) => `${arCount(n, AR_COURSE)} في مكتبتك`,
    discoverMore:   "اكتشف المزيد",
    browseCourses:  "تصفح الدورات",
    discoverSubtitle: (n) => `${arCount(n, AR_COURSE)} متاحة للإضافة إلى مجموعتك`,
    browseSubtitle:   (n) => `${arCount(n, AR_COURSE)} — اشترك لفتح المحتوى`,
    moreAvailable:  "المزيد متاح",
    exploreButton:  "استكشاف الدورة",
    unlockButton:   "فتح الدورة",
    subscribeButton:"اشترك لفتح المحتوى",
    search:         "ابحث في الدورات بالعنوان أو الوصف",
    sortMostPopular:"الأكثر شعبية",
    sortNewest:     "الأحدث",
    sortMostLessons:"أكثر الدروس",
    sortMostVideos: "أكثر الفيديوهات",
    locked:         "مقفل",
    unlocked:       "مفتوح",
    noResults:      "لا توجد دورات",
    noResultsSubtitle: "جرّب كلمة مختلفة أو امسح البحث الحالي.",
    resetSearch:    "مسح البحث",
    subscribeTitle: "اشترك لفتح الدورات",
    subscribeSubtitle: "اختر خطة للوصول إلى مكتبة دوراتنا البيطرية",
    viewPlans:      "عرض الخطط",
    active:         "نشط",
    planBanner:     (plan, count) => `خطة ${plan} — ${arCount(count, AR_COURSE)} مفتوحة`,
    unlockHint:     `انقر على "فتح الدورة" لأي دورة مقفلة لإضافتها إلى مجموعتك`,
    phases:         "مراحل",
    lessons:        "دروس",
    videos:         "فيديوهات",
    quickStart:     "بداية سريعة",
    phasesCount:    (n) => arCount(n, AR_PHASE),
    lessonsCount:   (n) => arCount(n, AR_LESSON),
    videosCount:    (n) => arCount(n, AR_VIDEO),
    minutes:        (n) => arCount(n, AR_MINUTE),
    hoursMinutes:   (h, m) => `${h} س ${m} د`,
    fallbackDescription: "منهج متكامل خطوة بخطوة يركّز على تقدّم ملموس ومستمر.",
    unlockShort:    "فتح",
    subscribeShort: "اشترك",
    openDetails:    "فتح تفاصيل الدورة",
    loadFailed:     "تعذّر تحميل الدورات",
    percentComplete: (pct) => `مكتمل ${pct}%`,
    unlockDialog: {
      title:             "افتح هذه الدورة",
      subtitle:          "النشرة لك للأبد. أضف الفيديو المرافق الآن، أو عد إليه في أي وقت.",
      leaflet:           "النشرة",
      withVideo:         "أضف الفيديو المرافق",
      withVideoHint:     (amount) => `+$${amount}`,
      total:             "الإجمالي",
      continueToPayment: "المتابعة إلى الدفع",
    },
    videoIncluded:  "الفيديو مضمّن",
    addVideo:       (amount) => `أضف الفيديو المرافق · $${amount}`,
    addVideoShort:  (amount) => `أضف الفيديو · $${amount}`,
    addVideoHint:   "شاهد الفيديو المرافق لهذه النشرة",
  },
  courseDetail: {
    backToCourses:     "العودة إلى الدورات",
    premiumCourse:     "دورة مميّزة",
    fallbackDescription:
      "مسار تعليمي متكامل ومميّز مصمّم لتحقيق نتائج ملموسة وتقدّم مستمر.",
    seriesNote:
      "كل دليل من كتابة الدكتورة ميرا تابت، الطبيبة البيطرية وأخصائية تغذية الحيوان، فاجمع السلسلة كاملة لتكون لديك إجابات موثوقة لكل مرحلة من حياة حيوانك الأليف.",
    notFound:          "الدورة غير موجودة",
    loadFailed:        "تعذّر تحميل الدورة",
    lockedBanner:      "هذه الدورة مقفلة من قبل الإدارة.",
    lockedReason:      (reason) => `السبب: ${reason}`,
    lockedError:       "هذه الدورة مقفلة حالياً من قبل الإدارة.",
    phasesChip:        (n) => arCount(n, AR_PHASE),
    videosChip:        (n) => arCount(n, AR_VIDEO),
    completedChip:     (done, total) => `مكتمل ${done} من ${total}`,
    courseProgress:    "تقدّم الدورة",
    curriculum:        "محتوى الدورة",
    curriculumSubtitle:"اتبع المراحل بالترتيب، وحدّد كل درس كمكتمل لتتبّع تقدّمك.",
    phasesSummary:     (n) => arCount(n, AR_PHASE),
    lessonsSummary:    (n) => arCount(n, AR_LESSON),
    liveSession:       "جلسة مباشرة",
    liveNow:           "مباشر الآن",
    scheduled:         "مجدولة",
    joinMeeting:       "الانضمام إلى الجلسة",
    nowPlaying:        "قيد المتابعة",
    itemTypes: { video: "فيديو", pdf: "ملف PDF", letter: "نص", quiz: "اختبار", survey: "استبيان" },
    pagesMeta:         (n) => arCount(n, AR_PAGE),
    questionsMeta:     (n) => arCount(n, AR_QUESTION),
    actions: {
      resume: "متابعة", watch: "مشاهدة", open: "فتح", read: "قراءة",
      startQuiz: "ابدأ الاختبار", takeSurvey: "املأ الاستبيان",
    },
    companion: {
      label:      "الفيديو المرافق",
      addOn:      "إضافة مدفوعة",
      watch:      "مشاهدة الفيديو",
      locked:     "الفيديو مقفل",
      lockedHint: "الفيديو المرافق إضافة مدفوعة لهذه النشرة. أضفه إلى اشتراكك لمشاهدته.",
      processing: "لا يزال الفيديو المرافق قيد المعالجة. عاود المحاولة بعد قليل.",
      buy:        (amount) => `افتح الفيديو · $${amount}`,
      buyHint:    (amount) => `أضف الفيديو المرافق لهذه النشرة مقابل $${amount}.`,
    },
    markComplete:      "تحديد كمكتمل",
    markIncomplete:    "إلغاء الإكمال",
    continueLearning:  "تابع التعلّم",
    continueSubtitle:  "تابع من درسك غير المكتمل التالي وحافظ على استمراريتك.",
    totalLessons:      "إجمالي الدروس",
    completedLabel:    "المكتملة",
    estimatedTime:     "المدة التقديرية",
    continueCta: {
      allDone: "أكملت جميع الدروس",
      video:   "متابعة درس الفيديو",
      pdf:     "متابعة درس الـ PDF",
      letter:  "متابعة الدرس النصي",
      quiz:    "ابدأ الاختبار",
      survey:  "املأ الاستبيان",
    },
    comments: {
      heading:      "تعليقات المجتمع",
      subtitle:     "شارك أفكارك، اطرح أسئلتك، وأعجب بالتعليقات المفيدة من المتعلّمين الآخرين.",
      placeholder:  "اكتب تعليقاً...",
      post:         "نشر",
      empty:        "لا توجد تعليقات بعد. كن أول من يبدأ النقاش.",
      reply:        "رد",
      replyTo:      (name) => `رد على ${name}...`,
      deleteConfirm:"هل تريد حذف هذا التعليق؟",
    },
    duration: {
      quickStart:   "بداية سريعة",
      minutes:      (n) => arCount(n, AR_MINUTE),
      hoursMinutes: (h, m) => `${h} س ${m} د`,
    },
    viewer: {
      lessonFallbackTitle:  "درس الدورة",
      contentFallbackTitle: "محتوى الدرس",
      pdfFallbackTitle:     "درس PDF",
      letterFallbackTitle:  "درس نصي",
      fullscreen:      "ملء الشاشة",
      close:           "إغلاق",
      open:            "فتح",
      rotateHint:      "أدر هاتفك أفقياً للحصول على أفضل تجربة مشاهدة.",
      videoUnavailable:"فيديو هذا الدرس غير متاح بعد.",
      pdfLoadFailed:   "تعذّر تحميل ملف PDF.",
      pdfLabel:        "PDF",
      englishVersion:  "النسخة الإنجليزية",
      arabicVersion:   "النسخة العربية",
      pdfLoading:            "جارٍ تحميل ملف PDF…",
      pdfInlineUnavailable:  "تعذّر عرض ملف PDF داخل الصفحة",
      pdfOpenNewTab:         "فتح في نافذة جديدة",
      pdfFullscreen:         "ملء الشاشة",
      pdfExitFullscreen:     "إنهاء ملء الشاشة",
      pdfDocument:           "مستند PDF",
      pdfMobileHint:         "اضغط الزر أدناه لفتح هذا الملف في عارض جهازك",
      pdfOpenButton:         "فتح ملف PDF",
      pdfMobileFooter:       "يُفتح في عارض PDF المدمج في متصفحك",
    },
    survey: {
      fallbackTitle:  "استبيان",
      gradedQuiz:     "اختبار مُقيَّم",
      survey:         "استبيان",
      questionCount:  (n) => arCount(n, AR_QUESTION),
      passingScore:   (n) => `درجة النجاح: ${n}%`,
      passed:         "ناجح!",
      notPassed:      "غير ناجح",
      passedBody:     "أحسنت! لقد اجتزت هذا الاختبار بنجاح.",
      failedBody:     (n) => `تحتاج إلى ${n}% للنجاح. راجع المادة وحاول مرة أخرى.`,
      correctOf:      (correct, total) => `${correct} من ${total} إجابة صحيحة`,
      reviewAnswers:  "راجع إجاباتك",
      yourAnswer:     "إجابتك:",
      correctAnswer:  "الإجابة الصحيحة:",
      noAnswer:       "بدون إجابة",
      notAutoGraded:  "إجابة نصية — لا تُصحَّح تلقائياً",
      retakeQuiz:     "إعادة الاختبار",
      close:          "إغلاق",
      thankYou:       "شكراً لك!",
      thankYouBody:   "تم إرسال إجابتك بنجاح.",
      submitAgain:    "إرسال مرة أخرى",
      questionFallback: (n) => `السؤال ${n}`,
      typeAnswer:     "اكتب إجابتك هنا...",
      chooseOption:   "اختر إجابة",
      cancel:         "إلغاء",
      submit:         "إرسال",
      submitting:     "جارٍ الإرسال...",
      submitFailed:   "تعذّر الإرسال. يرجى المحاولة مرة أخرى.",
    },
  },
  dashboard: {
    welcomeBack:     "مرحباً بعودتك",
    goodMorning:     "صباح الخير",
    goodAfternoon:   "مساء الخير",
    goodEvening:     "مساء الخير",
    subtitle:        "تابع رحلتك التعليمية في رعاية الحيوانات الأليفة. دوراتك بانتظارك.",
    totalVideos:     "إجمالي الفيديوهات",
    unlocked:        "المفتوحة",
    favorites:       "المفضلة",
    categories:      "الفئات",
    advancedLibrary: "المكتبة المتقدمة",
    myFavorites:     "مفضلتي",
    premiumContent:  "محتوى متميز منظم حسب الفئة",
    savedForAccess:  "فيديوهات حفظتها للوصول السريع",
    freePlan:        "أنت على الخطة المجانية",
    freePlanSubtitle:"قم بالترقية لفتح جميع الفيديوهات والدورات المتميزة.",
    activePlan:      "الخطة — نشطة",
    fullAccess:      "استمتع بوصولك الكامل إلى جميع المحتويات.",
    upgradePlan:     "ترقية الخطة",
    browseClasses:   "تصفح جميع الدورات",
    noVideos:        "لا توجد فيديوهات",
    noVideosSubtitle:"تابع قريباً — تُضاف محتويات جديدة بانتظام.",
    noFavorites:     "لا توجد مفضلات بعد",
    noFavoritesHint: "اضغط على ❤️ في أي فيديو لحفظه هنا.",
    foundation:      "الأساسيات",
    foundationDesc:  "ابنِ القاعدة",
    coaching:        "التدريب",
    coachingDesc:    "جلسات مباشرة",
    classes:         "الدورات",
    classesDesc:     "المكتبة الكاملة",
    favoritesDesc:   "الفيديوهات المحفوظة",
    videosChip:      (n) => `${n} فيديو`,
  },
  auth: {
    backHome: "الرئيسية",
    login: {
      metaTitle:       "تسجيل الدخول — Dr.Paw للتعليم البيطري",
      metaDescription: "سجّل دخولك إلى حسابك في Dr.Paw للوصول إلى دوراتك البيطرية ووحدات التعليم المستمر والدروس المتقدمة.",
      title:          "مرحباً بعودتك",
      subtitle:       "سجّل دخولك للوصول إلى دوراتك ومتابعة التعلّم.",
      email:          "البريد الإلكتروني",
      password:       "كلمة المرور",
      rememberMe:     "تذكّرني",
      forgotPassword: "هل نسيت كلمة المرور؟",
      loginButton:    "تسجيل الدخول",
      sessionExpired: "انتهت صلاحية جلستك. يُرجى تسجيل الدخول مرة أخرى.",
      failed:         "فشل تسجيل الدخول. يُرجى التحقق من بياناتك.",
      noAccount:      "ليس لديك حساب؟",
      signupLink:     "أنشئ حساباً مجاناً ←",
    },
    signup: {
      metaTitle:       "إنشاء حساب — دورات Dr.Paw للعناية بالحيوانات الأليفة",
      metaDescription: "انضم إلى Dr.Paw اليوم — التسجيل مجاني. دورات في العناية بالحيوانات الأليفة من إعداد أطباء بيطريين، ومجتمع من مربّي الحيوانات.",
      title:           "إنشاء حساب",
      subtitle:        "انضم مجاناً وابدأ التعلّم على يد أطباء بيطريين معتمدين.",
      fullName:        "الاسم الكامل",
      email:           "البريد الإلكتروني",
      password:        "كلمة المرور",
      confirmPassword: "تأكيد كلمة المرور",
      agreePrefix:     "أوافق على",
      terms:           "الشروط",
      and:             "و",
      privacy:         "سياسة الخصوصية",
      createAccount:   "أنشئ حسابي",
      haveAccount:     "هل لديك حساب بالفعل؟",
      signIn:          "تسجيل الدخول ←",
      successTitle:    "تهانينا! 🎉",
      successSubtitle: "جارٍ التوجيه إلى دوراتك…",
      passwordMismatch: "كلمتا المرور غير متطابقتين.",
      mustAgree:        "يُرجى الموافقة على الشروط وسياسة الخصوصية.",
      failed:           "فشل إنشاء الحساب. يُرجى المحاولة مرة أخرى.",
      strength: { weak: "ضعيفة", fair: "مقبولة", good: "جيدة", strong: "قوية" },
      badges:   { secure: "آمن", free: "حساب مجاني", noCard: "بدون بطاقة" },
    },
  },
  courseSelection: {
    back:                "العودة إلى الخطط",
    titleOne:            "اختر منشورك",
    titleMany:           "اختر منشوراتك",
    subtitleOne:         "اقرأ ما يتضمّنه كل دليل، ثم اختر المنشور الذي تريد فتحه.",
    subtitleMany:        (max) => `اقرأ ما يتضمّنه كل دليل، ثم اختر حتى ${arCount(max, AR_LEAFLET)} لفتحها.`,
    planLeaflets:        (n) => arCount(n, AR_LEAFLET),
    counter:             (selected, max) => `${selected} من ${max} في السلة`,
    owned:               "تملكه بالفعل",
    openOwned:           "افتح المنشور",
    select:              "أضف إلى السلة",
    selected:            "في سلّتك",
    limitReached:        (max) => `اخترت ${arCount(max, AR_LEAFLET)}. أزل واحداً لاختيار غيره.`,
    videoAvailable:      "يتوفّر فيديو مرافق",
    fallbackDescription: "لا يوجد وصف متاح",
    empty:               "لا توجد منشورات متاحة حالياً. يرجى المحاولة لاحقاً.",
    loadFailed:          "تعذّر تحميل المنشورات.",
    retry:               "أعد المحاولة",
    pickPrompt:          "أضف منشوراً إلى سلّتك للمتابعة",
    confirm:             "إتمام الشراء",
    withVideo:           "أضف الفيديو المرافق",
    withVideoHint:       (amount) => `+$${amount}`,
    videoLocked:         "اختر هذا المنشور أولاً",
    total:               "الإجمالي",
    totalHint:           (base, videos, addon) =>
      videos > 0
        ? `$${base} + ${videos} × $${addon} للفيديو`
        : `$${base}، بدون فيديو`,
  },
  common: {
    loading:  "جارٍ التحميل...",
    error:    "حدث خطأ",
    cancel:   "إلغاء",
    save:     "حفظ",
    close:    "إغلاق",
    back:     "رجوع",
    next:     "التالي",
    view:     "عرض",
    explore:  "استكشاف",
    perMonth: "/ شهر",
  },
};

// ─── Context ────────────────────────────────────────────────────
interface LanguageContextType {
  language: Locale;
  setLanguage: (lang: Locale) => void;
  t: DrPawT;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ─── Provider — reads from Redux, syncs document direction ──────
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const locale   = useSelector((state: RootState) => state.language.locale);
  const { pathname } = useLocation();

  // The admin panel is English-only. We pin the *effective* language here
  // rather than at the layout, so everything downstream — copy, direction,
  // the MUI RTL cache in DynamicMuiTheme, the document attributes — agrees
  // from one decision. The stored preference is left alone, so navigating
  // back out of /admin restores Arabic.
  const forceLtr = isLtrOnlyPath(pathname);
  const effectiveLocale: Locale = forceLtr ? "en" : locale;
  const isRTL = effectiveLocale === "ar";

  useEffect(() => {
    document.documentElement.dir  = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = effectiveLocale;

    // Keep Cairo loaded for as long as Arabic is the stored preference —
    // the admin editor's Arabic input fields still render in it even while
    // the surrounding panel is English.
    const FONT_ID = "drpaw-arabic-font";
    if (locale === "ar" && !document.getElementById(FONT_ID)) {
      const link = document.createElement("link");
      link.id   = FONT_ID;
      link.rel  = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }

    document.body.style.fontFamily = isRTL
      ? "'Cairo', 'Segoe UI', sans-serif"
      : "";
  }, [locale, effectiveLocale, isRTL]);

  const setLanguage = useCallback(
    (lang: Locale) => dispatch(switchLanguage(lang)),
    [dispatch]
  );

  const value = useMemo<LanguageContextType>(
    () => ({
      language: effectiveLocale,
      setLanguage,
      t: effectiveLocale === "ar" ? ar : en,
      isRTL,
    }),
    [effectiveLocale, isRTL, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────
export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};

export type { Locale as Language };
